import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getColumnSortOptions, getKanbanSortOption } from './kanbanUtils';
import type {
  EscrituraKanbanStatus,
  KanbanSort,
  KanbanSortDirection,
} from './types';

interface KanbanColumnSortMenuProps {
  status: EscrituraKanbanStatus;
  columnTitle: string;
  sort: KanbanSort;
  /** La columna está en su orden de fábrica, sin elección del usuario */
  isDefaultSort: boolean;
  onSortChange: (sort: KanbanSort) => void;
  onSortReset: () => void;
}

export const KanbanColumnSortMenu = ({
  status,
  columnTitle,
  sort,
  isDefaultSort,
  onSortChange,
  onSortReset,
}: KanbanColumnSortMenuProps) => {
  const sortOptions = getColumnSortOptions(status);
  const TriggerIcon = isDefaultSort
    ? ArrowUpDown
    : sort.sortDirection === 'asc'
      ? ArrowUp
      : ArrowDown;

  const handleFieldChange = (sortField: string) => {
    onSortChange({
      sortField,
      sortDirection: getKanbanSortOption(sortField)?.defaultDirection ?? 'asc',
    });
  };

  const handleDirectionChange = (sortDirection: string) => {
    onSortChange({
      sortField: sort.sortField,
      sortDirection: sortDirection as KanbanSortDirection,
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="size-7 shrink-0 p-0 text-muted-foreground hover:text-foreground"
          aria-label={`Ordenar columna ${columnTitle}`}
        >
          <TriggerIcon className="size-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Ordenar por</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={sort.sortField}
          onValueChange={handleFieldChange}
        >
          {sortOptions.map((option) => (
            <DropdownMenuRadioItem
              key={option.sortField}
              value={option.sortField}
            >
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={sort.sortDirection}
          onValueChange={handleDirectionChange}
        >
          <DropdownMenuRadioItem value="asc">Ascendente</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="desc">Descendente</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        {!isDefaultSort && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={onSortReset}
              className="text-sm text-muted-foreground"
            >
              Restaurar predeterminado
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
